/* Weekplan-viewer voor ouders — thema, uitstapjes en menu per week. Read-only.
 * Leidsters bewerken via het `WeekplanEditor` in de Groepsapp. */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, CalendarDays, Sparkles, MapPin, Utensils } from "lucide-react";
import { toast } from "sonner";

const DAGEN = [
  { key: "ma", label: "Maandag" },
  { key: "di", label: "Dinsdag" },
  { key: "wo", label: "Woensdag" },
  { key: "do", label: "Donderdag" },
  { key: "vr", label: "Vrijdag" },
];

function mondayOf(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

export default function PortaalWeekplanPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date().toISOString().slice(0, 10)));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    setLoading(true);
    api.get("/portaal/weekplan", { params: { week_start: weekStart } })
      .then(r => setData(r.data))
      .catch(e => { toast.error(formatApiError(e.response?.data?.detail)); setData(null); })
      .finally(() => setLoading(false));
  }, [status, user, weekStart]);

  const shift = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(mondayOf(d.toISOString().slice(0, 10)));
  };

  const label = useMemo(() => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 4);
    return `${start.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}`;
  }, [weekStart]);

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[color:var(--bone)]">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (user?.role !== "ouder") return <Navigate to="/app" replace />;

  const plans = data?.weekplans || [];

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain" data-testid="portaal-weekplan-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="weekplan-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--sage)" }}>
            <CalendarDays className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm leading-tight truncate">Weekplan</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Op de opvang</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 pb-24 space-y-4">
        <Card className="p-3 bg-white shadow-none flex items-center justify-between" data-testid="weekplan-nav">
          <Button variant="ghost" size="sm" onClick={() => shift(-1)} data-testid="weekplan-prev">← Vorige week</Button>
          <div className="text-sm font-semibold text-neutral-800" data-testid="weekplan-label">{label}</div>
          <Button variant="ghost" size="sm" onClick={() => shift(1)} data-testid="weekplan-next">Volgende →</Button>
        </Card>

        {loading && <div className="text-sm text-neutral-500 text-center py-6">Laden…</div>}

        {!loading && plans.length === 0 && (
          <Card className="p-6 bg-white text-center text-sm text-neutral-500" data-testid="weekplan-empty">
            Nog geen weekplan beschikbaar.
          </Card>
        )}

        {plans.map(p => {
          const hasContent = p.thema || p.uitstapjes || Object.values(p.menu || {}).some(d => d?.ochtend || d?.lunch || d?.middag);
          return (
            <Card key={p.group_id} className="p-4 bg-white shadow-none space-y-3" data-testid={`weekplan-group-${p.group_id}`}>
              <div className="flex items-center gap-2 border-b border-neutral-100 pb-2">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--sage)] font-semibold">Groep</div>
                <div className="font-medium text-neutral-800">{p.group_name || "Onbekend"}</div>
              </div>

              {!hasContent && (
                <div className="text-xs text-neutral-500 italic" data-testid={`weekplan-empty-${p.group_id}`}>
                  De leidsters hebben nog geen weekplan ingevuld voor deze groep.
                </div>
              )}

              {p.thema && (
                <div data-testid={`weekplan-thema-${p.group_id}`}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                    <Sparkles className="h-3 w-3" /> Thema van de week
                  </div>
                  <div className="text-sm text-neutral-800">{p.thema}</div>
                </div>
              )}

              {p.uitstapjes && (
                <div data-testid={`weekplan-uitstapjes-${p.group_id}`}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                    <MapPin className="h-3 w-3" /> Uitstapjes
                  </div>
                  <div className="text-sm text-neutral-800 whitespace-pre-line">{p.uitstapjes}</div>
                </div>
              )}

              {Object.values(p.menu || {}).some(d => d?.ochtend || d?.lunch || d?.middag) && (
                <div data-testid={`weekplan-menu-${p.group_id}`}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                    <Utensils className="h-3 w-3" /> Menu
                  </div>
                  <div className="space-y-1.5">
                    {DAGEN.map(d => {
                      const m = p.menu?.[d.key] || {};
                      if (!m.ochtend && !m.lunch && !m.middag) return null;
                      return (
                        <div key={d.key} className="text-sm bg-neutral-50 rounded p-2" data-testid={`weekplan-menu-${p.group_id}-${d.key}`}>
                          <div className="font-medium text-neutral-800 text-xs">{d.label}</div>
                          {m.ochtend && <div className="text-neutral-600 text-xs">• Ochtend: {m.ochtend}</div>}
                          {m.lunch && <div className="text-neutral-600 text-xs">• Lunch: {m.lunch}</div>}
                          {m.middag && <div className="text-neutral-600 text-xs">• Middag: {m.middag}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </main>
    </div>
  );
}
